import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet } from 'react-router-dom';
import { AppShell as MineralAppShell, AppShellProps, Box } from '@mineral/core';
import { AppBar } from 'components/AppBar';
import { Footer } from 'components/Footer';
import { Navigation } from 'components/Navigation';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { appContext } from 'api/ApplicationContext/AppContext';
import LoadingScreenComp from 'Utils/components/LoadingScreenComp';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { defaultURL, IDefaultUrl } from 'types/Itypes';
import { useState } from 'react';
import { AllowedFields } from 'App';

export enum FloatingDrawerPosition {
  LEFT,
  RIGHT,
  TOP,
  BOTTOM,
  NONE,
}
export const UpdateUrlHistory = React.createContext<
  React.Dispatch<React.SetStateAction<IDefaultUrl>>
>(() => {});
export const UrlHistory = React.createContext<IDefaultUrl>(defaultURL);

export const AppShell: React.FC<{
  appShellProps?: AppShellProps;
  loading?: boolean;
}> = ({ appShellProps, loading }) => {
  const oktaAuthDetails = useOktaAuth();
  const [floatingDrawer, setFloatingDrawer] =
    useLocalStorage<FloatingDrawerPosition>(
      'floatingDrawer',
      FloatingDrawerPosition.NONE
    );
  const [history, setHistory] = useState<IDefaultUrl>(defaultURL);
  React.useEffect(() => {
    setFloatingDrawer(FloatingDrawerPosition.LEFT);
  });

  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development' && !loading) {
      const { authState, oktaAuth } = oktaAuthDetails;
      if (!authState) {
        return;
      }

      if (!authState?.isAuthenticated) {
        const originalUri = toRelativeUrl(
          window.location.href,
          window.location.origin
        );
        oktaAuth.setOriginalUri(originalUri);
        void oktaAuth.signInWithRedirect();
      }
    }
  }, [loading, oktaAuthDetails]);
  const allowedFields = React.useContext(AllowedFields);

  if (
    process.env.NODE_ENV !== 'development' &&
    (!oktaAuthDetails?.authState ||
      !oktaAuthDetails?.authState?.isAuthenticated ||
      !allowedFields)
  ) {
    return <LoadingScreenComp />;
  }

  return (
    <>
      <Helmet
        titleTemplate={`%s | ${appContext.AppTitle}`}
        defaultTitle={appContext.AppTitle}
      />
      <UrlHistory.Provider value={history}>
        <UpdateUrlHistory.Provider value={setHistory}>
          <MineralAppShell
            appBar={<AppBar />}
            footer={<Footer />}
            {...appShellProps}>
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background:
                  'conic-gradient(from 145.9deg at 52.72% 30.87%, #97E1D4 -190.84deg, #6284FF 30.46deg, #FF72B6 51.42deg, #F9E5E8 94.7deg, #97E1D4 169.16deg, #6284FF 390.46deg)',
                opacity: '10%',
                filter: ' blur(121.819px)',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                height: [
                  FloatingDrawerPosition.LEFT,
                  FloatingDrawerPosition.NONE,
                  FloatingDrawerPosition.RIGHT,
                ].includes(floatingDrawer)
                  ? 'calc(100vh)' // - 65px)'
                  : 'auto',
                width: [
                  FloatingDrawerPosition.LEFT,
                  FloatingDrawerPosition.NONE,
                  FloatingDrawerPosition.RIGHT,
                ].includes(floatingDrawer)
                  ? 'auto'
                  : '100vw',
                top: [
                  FloatingDrawerPosition.LEFT,
                  FloatingDrawerPosition.NONE,
                  FloatingDrawerPosition.RIGHT,
                  FloatingDrawerPosition.TOP,
                ].includes(floatingDrawer)
                  ? 0 //'65px'
                  : 'calc(100vh - 64px - 10px)',
                left: [FloatingDrawerPosition.LEFT].includes(floatingDrawer)
                  ? 0
                  : 'unset',
                right: [FloatingDrawerPosition.RIGHT].includes(floatingDrawer)
                  ? '48px'
                  : 'unset',
                display: 'flex',
                alignItems: [
                  FloatingDrawerPosition.LEFT,
                  FloatingDrawerPosition.NONE,
                  FloatingDrawerPosition.RIGHT,
                ].includes(floatingDrawer)
                  ? 'center'
                  : 'unset',
                justifyContent: [
                  FloatingDrawerPosition.LEFT,
                  FloatingDrawerPosition.NONE,
                  FloatingDrawerPosition.RIGHT,
                ].includes(floatingDrawer)
                  ? 'unset'
                  : 'center',
                ml: 1,
              }}>
              <Navigation dockPosition={floatingDrawer} />
            </Box>
            {!loading && <Outlet />}
            {loading && <LoadingScreenComp />}
          </MineralAppShell>
        </UpdateUrlHistory.Provider>
      </UrlHistory.Provider>
    </>
  );
};
